import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import insights from "@/data/insightsData";

export default function InsightDetail() {
  const { slug } = useParams();
  const insight = insights.find((i) => i.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!insight) return <Navigate to="/insights" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="insight-detail-page">
      <Navbar />

      <section className="relative pt-28 md:pt-36 pb-6 md:pb-10" data-testid="insight-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* 1. Title */}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.05,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="font-body text-[32px] md:text-[42px] font-semibold text-[#08122a] tracking-tight leading-[1.15] mb-6"
            data-testid="insight-title"
          >
            {insight.title}
          </motion.h1>

          {/* 2. Back to Insights */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mb-4"
          >
            <Link
              to="/insights"
              data-testid="back-to-insights"
              className="inline-flex items-center gap-2 font-body text-[13px] font-medium text-[#94a3b8] hover:text-[#4F6BFF] transition-colors duration-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Insights
            </Link>
          </motion.div>

          {/* 3. Date + Category */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex items-center gap-3 mb-10"
          >
            <span className="font-body text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4F6BFF]">
              {insight.category}
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span className="font-body text-[12px] text-[#94a3b8]">
              {insight.date}
            </span>
          </motion.div>

          {/* 4. Image */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="rounded-2xl overflow-hidden h-64 md:h-[420px] relative mb-12"
          >
            <img
              src={insight.image}
              alt={insight.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
          </motion.div>

          {/* 5. Article text */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="space-y-6"
            data-testid="insight-body"
          >
            {insight.content.map((block, i) =>
              block.type === "heading" ? (
                <h2
                  key={i}
                  className="font-body text-[22px] md:text-[24px] font-semibold text-[#08122a] tracking-tight leading-[1.25] pt-4"
                >
                  {block.text}
                </h2>
              ) : (
                <p
                  key={i}
                  className="font-body text-[16px] leading-[1.85] text-[#334155]"
                >
                  {block.text}
                </p>
              )
            )}
          </motion.div>

          {/* Bottom back link */}
          <div className="h-px bg-slate-100 mt-8 mb-5" />
          <Link
            to="/insights"
            data-testid="bottom-back-link"
            className="inline-flex items-center gap-2 font-body text-[13px] font-medium text-[#94a3b8] hover:text-[#4F6BFF] transition-colors duration-200"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Insights
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
